import { useState, type Dispatch, type SetStateAction } from 'react';
import { Card, Form } from 'react-bootstrap';
import Flex from '@/components/common/Flex';
import classNames from 'classnames';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { checkListConfig } from '@/app/Crawl/config/checkListConfig';
import { AiTwotoneAlert } from 'react-icons/ai';
import { getColor } from '@/helpers/utils';
import SubtleBadge from '@/components/common/SubtleBadge';
import { useInsertCrawlToDoMutation } from '@/app/Crawl/api/pagesApi';
import { errorMessage, successMessage } from '@/utils/toast';
import { MdOutlineOpenInNew } from 'react-icons/md';
import useDomain from '@/hooks/useDomain';

type ChecklistMode = 'danger' | 'warning' | 'info';

type ChecklistDefinition = {
  title: string;
  link: string;
  show_title?: string;
  priority?: string;
};

type ChecklistConfig = {
  errors: ChecklistDefinition[];
  warning: ChecklistDefinition[];
  info: ChecklistDefinition[];
};

type TodoItem = {
  flag: string;
  condition?: number;
  count?: number | string;
};

const CheckListFounder = (
  name: string,
  mode: ChecklistMode
): ChecklistDefinition | null => {
  const config = checkListConfig(null) as unknown as ChecklistConfig;
  switch (mode) {
    case 'danger':
      return (
        config.errors.find(
          (column: ChecklistDefinition) => column.title === name
        ) ?? null
      );
    case 'warning':
      return (
        config.warning.find(
          (column: ChecklistDefinition) => column.title === name
        ) ?? null
      );
    case 'info':
      return (
        config.info.find(
          (column: ChecklistDefinition) => column.title === name
        ) ?? null
      );

    default:
      return null;
  }
};

type ToDoListItemProps = {
  item: TodoItem;
  mode: ChecklistMode;
  index: number;
  length: number;
  handleChange: (item: TodoItem, completed: boolean) => Promise<void>;
  job_page?: string | null;
  page_url?: string | null;
  setTodoItems?: Dispatch<SetStateAction<TodoItem[]>>;
};

const ToDoListItem = ({
  item,
  mode,
  index,
  handleChange,
  job_page,
  page_url
}: ToDoListItemProps) => {
  const issue_info = CheckListFounder(item.flag, mode);
  const { domain, domain_slug } = useDomain();

  if (issue_info) {
    return (
      <Flex
        justifyContent="between"
        className="hover-actions-trigger btn-reveal-trigger px-x1 hover-bg-100 border-200 border-top"
        alignItems="center"
      >
        <Form.Check
          className="py-2 my-2 d-flex align-items-center"
          id={`todoItem-${index}`}
        >
          <Form.Check.Input
            type="checkbox"
            defaultChecked={item.condition === 1 ? true : false}
            onChange={e => handleChange(item, e.target.checked)}
            className={classNames(
              `mt-0 rounded-circle form-check-input-${
                issue_info.priority === 'high'
                  ? 'danger'
                  : issue_info.priority === 'medium'
                    ? 'warning'
                    : 'info'
              } form-check-line-through`
            )}
          />
          <Form.Check.Label className="flex-1 mb-0 ms-3">
            {issue_info?.show_title}{' '}
            <SubtleBadge bg="secondary" className="me-2">
              {item.count}
            </SubtleBadge>
          </Form.Check.Label>
        </Form.Check>

        <div className="d-flex align-items-center">
          <a
            href={`/crawl/check-list/${job_page}/${domain_slug}/${domain}?issues=${issue_info.link}&FByUrl=[[and,equals,${page_url}]]`}
            target="_blank"
            rel="noreferrer"
            className="nav-link"
          >
            <MdOutlineOpenInNew />
          </a>
        </div>
        <style>
          {`
          .badge-subtle-secondary {
            height: 20px;
            font-size: 11px;
          }    
          `}
        </style>
      </Flex>
    );
  }
};

type ToDoListProps = {
  data?: TodoItem[];
  title: string;
  tooltip: string;
  numOfIssue?: number | string;
  mode?: ChecklistMode;
  job_page?: string | null;
  page_url?: string | null;
  path?: string;
};

const ToDoList = ({
  data,
  title,
  tooltip,
  numOfIssue,
  mode = 'danger',
  job_page,
  page_url
}: ToDoListProps) => {
  const { domain_slug } = useDomain();
  const [todoItems, setTodoItems] = useState<TodoItem[]>(data ?? []);
  const [insertToDo] = useInsertCrawlToDoMutation();

  const handleChange = async (
    item: TodoItem,
    completed: boolean
  ): Promise<void> => {
    const condition = completed ? 1 : 0;
    await insertToDo({
      job_page,
      domain_slug,
      page_url,
      flag: item.flag,
      type: 'url',
      count: item.count,
      condition
    })
      .unwrap()
      .then(() => {
        const updatedTodos = todoItems.map(todo =>
          todo.flag === item.flag ? { ...todo, condition } : todo
        );
        setTodoItems(updatedTodos);
        successMessage('عملیات با موفقیت انجام شد.');
      })
      .catch((error: unknown) => {
        console.error('Error fetching data:', error);
        errorMessage('خطای غیر منتظره ای رخ داد');
      });
  };

  return (
    <Card className="h-100">
      <FalconCardHeader
        title={
          <span>
            <AiTwotoneAlert size={20} className="me-2" color={getColor(mode)} />
            {title}
            <span className="fs--1">
              <HelpTooltip html={tooltip} />
            </span>
            <SubtleBadge bg={mode} className="me-2 float-end">
              {numOfIssue}
            </SubtleBadge>
          </span>
        }
        titleTag="h6"
        className="py-2"
      />
      <Card.Body className="p-0 overflow-hidden">
        {numOfIssue == 0 ? (
          <div className="px-4">
            <SubtleBadge bg={mode} className="w-100 float-end">
              <span>
                هیچ{' '}
                {title === 'هشدار'
                  ? 'هشداری'
                  : title === 'اطلاعیه'
                    ? 'اطلاعیه ای'
                    : 'خطایی'}{' '}
                وجود ندارد.
              </span>
            </SubtleBadge>
          </div>
        ) : (
          todoItems.map((item, index) => (
            <ToDoListItem
              key={1}
              item={item}
              index={index}
              length={todoItems.length}
              mode={mode}
              job_page={job_page}
              page_url={page_url}
              setTodoItems={setTodoItems}
              handleChange={handleChange}
            />
          ))
        )}
      </Card.Body>
    </Card>
  );
};

export default ToDoList;
